import InformationIcon from '@atlaskit/icon/utility/information';
import { Box, Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Select, { FormatOptionLabelMeta, StylesConfig } from '@databases/atlaskit/Select';
import {
  DatabaseFilterUsageType,
  DatabaseFilterType,
} from '@databases/types/database/view';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import useView from '../../hooks/useView';
import { messages } from '../../i18n';

const CONTROL_OPTION_WIDTH = '83px';

const OptionLabelStackStyles = xcss({
  gap: 'space.075',
});

const OptionLabelInlineStyles = xcss({
  alignItems: 'center',
  gap: 'space.050',
});

const labels: Record<
  DatabaseFilterUsageType,
  { id: string; defaultMessage: string; description: string }
> = {
  [DatabaseFilterType.AND]: messages['view-settings-panel-filter.and-filter-type-label'],
  [DatabaseFilterType.OR]: messages['view-settings-panel-filter.or-filter-type-label'],
};

type UsageOption = {
  label: string;
  value: DatabaseFilterUsageType;
};

const infoText: Record<
  DatabaseFilterUsageType,
  { id: string; defaultMessage: string; description: string }
> = {
  [DatabaseFilterType.AND]:
    messages['view-settings-panel-filter.and-filter-type-info-text'],
  [DatabaseFilterType.OR]:
    messages['view-settings-panel-filter.or-filter-type-info-text'],
};

export interface ViewSettingsFilterUsagePickerProps {
  handleChangeFilterType: (type: DatabaseFilterUsageType) => void;
  isEditable?: boolean;
}

const ViewSettingsFilterUsagePicker: FunctionComponent<
  ViewSettingsFilterUsagePickerProps
> = ({ handleChangeFilterType, isEditable }) => {
  const { view } = useView();
  const { formatMessage } = useIntl();
  const filterObject = view.settings.filter;

  const options: UsageOption[] = Object.keys(labels).map((type) => ({
    label: formatMessage(labels[type as DatabaseFilterUsageType]),
    value: type as DatabaseFilterUsageType,
  }));

  const customStyles: StylesConfig<UsageOption> = {
    control: (provided, state) => ({
      ...provided,
      // TODO: after atlaskit version bump, this style override should no longer be needed
      border: `1px solid ${
        state.isFocused
          ? token('color.border.focused', '#388BFF')
          : token('color.border.input', '#8590A2')
      }`,
      width: CONTROL_OPTION_WIDTH,
    }),
    menu: (provided) => ({
      ...provided,
      width: 'max-content',
    }),
  };

  const valueLabel = useMemo(() => {
    return options.find((option) => option.value === filterObject?.type);
  }, [filterObject?.type, options]);

  const handleChange = useCallback(
    (option: UsageOption | null) => {
      if (!option) {
        return;
      }
      handleChangeFilterType(option.value);
    },
    [handleChangeFilterType]
  );

  const formatOptionLabel = useCallback(
    (option: UsageOption, meta: FormatOptionLabelMeta<UsageOption>) => {
      const isSelected = option.value === valueLabel?.value;
      if (meta.context === 'menu') {
        return (
          <Stack xcss={OptionLabelStackStyles}>
            <Box>{option.label}</Box>
            <Inline xcss={OptionLabelInlineStyles}>
              <InformationIcon
                color={
                  isSelected
                    ? token('color.icon.accent.blue', '#1D7AFC')
                    : token('color.icon.subtle', '#5E6C84')
                }
                label={''}
              />
              <Text
                size="small"
                color={isSelected ? 'color.text.selected' : 'color.text.subtlest'}
              >
                {formatMessage(infoText[option.value])}
              </Text>
            </Inline>
          </Stack>
        );
      } else {
        return <Text>{option.label}</Text>;
      }
    },
    [formatMessage, valueLabel?.value]
  );

  return (
    <Box
      testId="filter-type-dropdown-menu"
      xcss={xcss({ paddingInline: isEditable ? 'space.0' : 'space.075' })}
    >
      {isEditable ? (
        <Select<UsageOption>
          inputId="filter-type-dropdown"
          value={valueLabel}
          placeholder=""
          options={options}
          onChange={handleChange}
          formatOptionLabel={formatOptionLabel}
          styles={customStyles}
          isSearchable={false}
        ></Select>
      ) : (
        <>{valueLabel?.label}</>
      )}
    </Box>
  );
};

export default ViewSettingsFilterUsagePicker;
